import { useState, useMemo, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import type { MockJobWorkOrder } from '@/mocks/billing';
import AppLayout from '@/components/feature/AppLayout';
import { jobWorkService } from '@/services/jobWorkService';
import { getProductionOrderById, type ProductionOrderResponse } from '@/api/productionOrder.api';

type JWOStatus = MockJobWorkOrder['status'];

const STATUS_CONFIG: Record<JWOStatus, { label: string; bg: string; text: string }> = {
  DRAFT: { label: 'Draft', bg: 'bg-slate-100 text-slate-700 border-slate-200', text: 'text-slate-700' },
  ISSUED: { label: 'Challan Issued', bg: 'bg-blue-50 text-blue-700 border-blue-200', text: 'text-blue-700' },
  IN_PROCESS: { label: 'At Sub-Contractor', bg: 'bg-amber-50 text-amber-700 border-amber-200', text: 'text-amber-700' },
  RECEIVED: { label: 'Received', bg: 'bg-emerald-50 text-emerald-700 border-emerald-200', text: 'text-emerald-700' },
  PARTIALLY_RECEIVED: { label: 'Partial Return', bg: 'bg-teal-50 text-teal-700 border-teal-200', text: 'text-teal-700' },
  CANCELLED: { label: 'Cancelled', bg: 'bg-rose-50 text-rose-700 border-rose-200', text: 'text-rose-700' },
};

export default function JobWorkDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [jwo, setJwo] = useState<MockJobWorkOrder | null>(null);
  const [loading, setLoading] = useState(true);

  // Receipt slide-over state
  const [receiptOpen, setReceiptOpen] = useState(searchParams.get('action') === 'receipt');
  const [receiptItems, setReceiptItems] = useState<Record<string, { receivedQty: number; rejectedQty: number; notes: string }>>({});
  const [receiptError, setReceiptError] = useState('');
  const [showCostingModal, setShowCostingModal] = useState(false);
  const [pendingReceipt, setPendingReceipt] = useState(false);
  const [showPartialConfirm, setShowPartialConfirm] = useState(false);

  // Fetch live JWO data
  const loadJwoDetail = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await jobWorkService.getById(id);
      if (data) {
        setJwo(data);
      }
    } catch (err) {
      console.error('Error fetching JWO details:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadJwoDetail();
  }, [id]);

  if (loading) {
    return (
      <AppLayout>
        <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center w-full">
          <div className="flex items-center gap-3 text-slate-600 text-sm font-medium">
            <i className="ri-loader-4-line animate-spin text-2xl text-blue-600" />
            <span>Loading job work details...</span>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (!jwo) {
    return (
      <AppLayout>
        <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center w-full">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-2xl bg-amber-50 border border-amber-200 mb-4">
              <i className="ri-error-warning-line text-3xl text-amber-500" />
            </div>
            <h1 className="text-xl font-bold text-[#1e293b]">Job Work Order Not Found</h1>
            <p className="text-sm text-slate-500 mt-1">The requested JWO could not be found.</p>
            <Link to="/manufacturing/jobwork" className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-xl bg-[#2563eb] text-white text-sm font-semibold hover:bg-blue-700 cursor-pointer whitespace-nowrap shadow-sm">
              <i className="ri-arrow-left-line" /> Back to Job Work
            </Link>
          </div>
        </div>
      </AppLayout>
    );
  }

  const statusCfg = STATUS_CONFIG[jwo.status] || { label: jwo.status, bg: 'bg-slate-100 text-slate-700 border-slate-200' };
  const subContractor = { name: jwo.subContractorName || 'Sub-Contractor', gstin: jwo.subContractorGSTIN || 'N/A' };
  const [productionOrder, setProductionOrder] = useState<ProductionOrderResponse | null>(null);

  useEffect(() => {
    if (!jwo?.productionOrderId) {
      setProductionOrder(null);
      return;
    }
    let active = true;
    getProductionOrderById(jwo.productionOrderId)
      .then((res) => {
        if (active && res.success && res.data) {
          setProductionOrder(res.data);
        } else if (active) {
          setProductionOrder(null);
        }
      })
      .catch(() => {
        if (active) setProductionOrder(null);
      });
    return () => {
      active = false;
    };
  }, [jwo?.productionOrderId]);

  const totalMaterialValue = jwo.items.reduce((s, i) => s + i.totalValue, 0);

  // Initialize receipt items
  const initReceipt = () => {
    const items: Record<string, { receivedQty: number; rejectedQty: number; notes: string }> = {};
    jwo.items.forEach((item) => {
      items[item.id] = { receivedQty: item.pendingQty, rejectedQty: 0, notes: '' };
    });
    setReceiptItems(items);
    setReceiptError('');
    setReceiptOpen(true);
  };

  const handleIssueChallan = () => {
    navigate('/sales/challans', { state: { fromJWO: jwo } });
  };

  const handleRecordReceipt = () => {
    // Validate
    let hasError = false;
    jwo.items.forEach((item) => {
      const ri = receiptItems[item.id];
      if (!ri) { hasError = true; return; }
      const total = ri.receivedQty + ri.rejectedQty;
      if (total > item.pendingQty) { hasError = true; setReceiptError(`Cannot exceed pending qty for ${item.itemName}`); return; }
    });
    if (hasError && !receiptError) {
      setReceiptError('Invalid quantities entered');
      return;
    }

    // Check for partial receipt
    const isPartial = jwo.items.some((item) => {
      const ri = receiptItems[item.id];
      if (!ri) return true;
      return ri.receivedQty < item.pendingQty;
    });

    if (isPartial) {
      setPendingReceipt(true);
      setShowPartialConfirm(true);
      return;
    }

    applyReceipt();
  };

  const applyReceipt = async () => {
    try {
      const res = await jobWorkService.recordReceipt(jwo.id, receiptItems);
      if (res && res.success) {
        await loadJwoDetail();
      } else {
        let allReceived = true;
        jwo.items.forEach((item) => {
          const ri = receiptItems[item.id];
          if (!ri || ri.receivedQty + ri.rejectedQty > item.pendingQty) return;
          item.receivedQty += ri.receivedQty;
          item.rejectedQty += ri.rejectedQty;
          item.pendingQty = item.sentQty - item.receivedQty - item.rejectedQty;
          if (item.pendingQty > 0) allReceived = false;
        });

        if (allReceived) {
          jwo.status = 'RECEIVED';
          jwo.actualReturnDate = new Date().toISOString().slice(0, 10);
        } else {
          jwo.status = 'PARTIALLY_RECEIVED';
        }
        setJwo({ ...jwo });
      }
    } catch (e) {
      console.error('applyReceipt error:', e);
    }

    setReceiptOpen(false);
    setShowPartialConfirm(false);
    setPendingReceipt(false);

    // Check if job charges should be added to production cost
    if (jwo.productionOrderId && jwo.jobCharges > 0) {
      setShowCostingModal(true);
    }
  };

  const handleAddToProductionCost = () => {
    setShowCostingModal(false);
    const ToastContext = (window as any).__toast;
    if (ToastContext?.success) {
      ToastContext.success(`₹${jwo.jobCharges.toLocaleString('en-IN')} job work charges added to production cost`);
    }
  };

  const handleSkipCosting = () => {
    setShowCostingModal(false);
  };

  const handleCancel = async () => {
    try {
      const res = await jobWorkService.cancel(jwo.id);
      if (res && res.success) {
        await loadJwoDetail();
      } else {
        jwo.status = 'CANCELLED';
        setJwo({ ...jwo });
      }
    } catch (e) {
      console.error('handleCancel error:', e);
    }
  };

  const canIssue = jwo.status === 'DRAFT';
  const canReceive = jwo.status === 'IN_PROCESS' || jwo.status === 'ISSUED';
  const hasReceiptData = jwo.status === 'IN_PROCESS' || jwo.status === 'ISSUED' || jwo.status === 'RECEIVED' || jwo.status === 'PARTIALLY_RECEIVED';

  // Build timeline
  const timelineItems = [
    { label: 'PO Created', date: jwo.createdAt, active: true },
    { label: 'Sent to Sub-Contractor', date: jwo.challanNumber ? jwo.date : null, active: !!jwo.challanNumber },
    { label: jwo.status === 'RECEIVED' ? 'Received' : jwo.status === 'PARTIALLY_RECEIVED' ? 'Partially Received' : 'Awaiting Return', date: jwo.actualReturnDate, active: jwo.status === 'RECEIVED' || jwo.status === 'PARTIALLY_RECEIVED' },
  ];

  return (
    <AppLayout>
      <div className="min-h-screen bg-[#f8fafc] py-6 px-4 md:px-6 w-full">
        <div className="w-full">
          {/* Header & Back Button */}
          <div className="flex items-center gap-3 mb-6">
            <button
              onClick={() => navigate('/manufacturing/jobwork')}
              className="w-9 h-9 flex items-center justify-center rounded-lg bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-700 cursor-pointer shadow-sm transition-colors"
              title="Back to Job Work Orders"
            >
              <i className="ri-arrow-left-line text-lg" />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-[#1e293b]">{jwo.jwoNumber}</h1>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-[11px] font-bold border ${statusCfg.bg}`}>
                  {statusCfg.label}
                </span>
              </div>
              <p className="text-sm text-slate-500 font-medium mt-0.5">
                Issued on {new Date(jwo.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}
              </p>
            </div>

            {/* Header Right Action Cluster */}
            <div className="flex items-center gap-2.5 ml-auto flex-wrap">
              {canIssue && (
                <button
                  onClick={handleIssueChallan}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#2563eb] hover:bg-blue-700 text-white text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap shadow-sm"
                >
                  <i className="ri-file-list-3-line text-base" /> Issue Challan
                </button>
              )}
              {canReceive && (
                <button
                  onClick={initReceipt}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap shadow-sm"
                >
                  <i className="ri-inbox-archive-line text-base" /> Record Receipt
                </button>
              )}
              {jwo.challanNumber && (
                <button
                  onClick={() => window.print()}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold border border-slate-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-printer-line text-base" /> Print Challan
                </button>
              )}
              {canIssue && (
                <button
                  onClick={handleCancel}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-700 text-sm font-semibold border border-rose-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-close-circle-line text-base" /> Cancel
                </button>
              )}
            </div>
          </div>

          {/* Sub-contractor info grid */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-6 shadow-sm">
            <h3 className="text-sm font-bold text-[#1e293b] mb-4 pb-2 border-b border-slate-100">Order Overview</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div>
                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Sub-Contractor</p>
                <p className="text-sm font-bold text-[#1e293b] mt-1">{jwo.subContractorName}</p>
                <p className="text-xs text-slate-500 font-medium">GSTIN: {jwo.subContractorGSTIN || 'N/A'}</p>
              </div>
              <div>
                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Job Description</p>
                <p className="text-sm font-semibold text-[#1e293b] mt-1">{jwo.jobDescription}</p>
                {jwo.notes && <p className="text-xs text-slate-500 mt-1 font-medium">{jwo.notes}</p>}
              </div>
              <div>
                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Schedule &amp; Charges</p>
                <p className="text-sm font-semibold text-[#1e293b] mt-1">Expected Return: {new Date(jwo.expectedReturnDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                {jwo.actualReturnDate && <p className="text-sm text-emerald-700 font-semibold">Actual Return: {new Date(jwo.actualReturnDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>}
                <p className="text-sm font-bold text-[#2563eb] mt-1">Job Charges: ₹{jwo.jobCharges.toLocaleString('en-IN')}</p>
              </div>
            </div>
          </div>

          {/* Materials Sent */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-6 shadow-sm">
            <h2 className="text-sm font-bold text-[#1e293b] mb-4">Materials Sent</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50/70 border-b border-slate-200">
                    <th className="text-left px-3 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">#</th>
                    <th className="text-left px-3 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Item</th>
                    <th className="text-left px-3 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">HSN</th>
                    <th className="text-right px-3 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Sent Qty</th>
                    <th className="text-left px-3 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Unit</th>
                    <th className="text-right px-3 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Rate (₹)</th>
                    <th className="text-right px-3 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Value (₹)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {jwo.items.map((item, idx) => (
                    <tr key={item.id} className="hover:bg-slate-50/60 transition-colors">
                      <td className="px-3 py-2.5 text-slate-400 font-medium">{idx + 1}</td>
                      <td className="px-3 py-2.5 font-bold text-[#1e293b]">{item.itemName}</td>
                      <td className="px-3 py-2.5 text-slate-500 font-mono text-xs">{item.hsnCode}</td>
                      <td className="px-3 py-2.5 text-right font-bold text-[#1e293b]">{item.sentQty.toLocaleString('en-IN')}</td>
                      <td className="px-3 py-2.5 text-slate-600 font-medium">{item.unit}</td>
                      <td className="px-3 py-2.5 text-right text-slate-700 font-medium">₹{item.rate.toLocaleString('en-IN')}</td>
                      <td className="px-3 py-2.5 text-right font-bold text-[#1e293b]">₹{item.totalValue.toLocaleString('en-IN')}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-200">
                    <td colSpan={6} className="px-3 py-3 text-right font-bold text-slate-700">Total Material Value:</td>
                    <td className="px-3 py-3 text-right font-bold text-[#2563eb] text-base">₹{totalMaterialValue.toLocaleString('en-IN')}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Receipt Status (if IN_PROCESS+) */}
          {hasReceiptData && (
            <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-6 shadow-sm">
              <h2 className="text-sm font-bold text-[#1e293b] mb-4">Receipt Status</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50/70 border-b border-slate-200">
                      <th className="text-left px-3 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Item</th>
                      <th className="text-right px-3 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Sent</th>
                      <th className="text-right px-3 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Received</th>
                      <th className="text-right px-3 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Pending</th>
                      <th className="text-right px-3 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Rejected</th>
                      <th className="text-left px-3 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Progress</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {jwo.items.map((item) => {
                      const pct = item.sentQty > 0 ? Math.round((item.receivedQty / item.sentQty) * 100) : 0;
                      return (
                        <tr key={item.id} className="hover:bg-slate-50/60 transition-colors">
                          <td className="px-3 py-2.5 font-bold text-[#1e293b]">{item.itemName}</td>
                          <td className="px-3 py-2.5 text-right text-slate-600 font-medium">{item.sentQty.toLocaleString('en-IN')}</td>
                          <td className="px-3 py-2.5 text-right font-bold text-emerald-700">{item.receivedQty.toLocaleString('en-IN')}</td>
                          <td className="px-3 py-2.5 text-right font-bold text-amber-700">{item.pendingQty.toLocaleString('en-IN')}</td>
                          <td className="px-3 py-2.5 text-right font-bold text-rose-700">{item.rejectedQty.toLocaleString('en-IN')}</td>
                          <td className="px-3 py-2.5 min-w-[140px]">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full transition-all ${pct >= 100 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-500' : 'bg-slate-300'}`}
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                              <span className="text-xs font-bold text-slate-500 w-9 text-right">{pct}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Linked Documents */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {productionOrder && (
              <Link
                to={`/manufacturing/production-orders/${productionOrder.id}`}
                className="bg-white border border-slate-200 rounded-2xl p-5 hover:border-blue-300 transition-colors shadow-sm cursor-pointer block"
              >
                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Linked Production Order</p>
                <div className="flex items-center gap-2.5 mt-2">
                  <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-blue-50 text-blue-600 shrink-0">
                    <i className="ri-stack-line text-base" />
                  </div>
                  <span className="font-bold text-[#2563eb] text-sm">{productionOrder.poNumber}</span>
                  <span className="text-xs text-slate-500 font-medium">— {productionOrder.itemName || 'Production Order'}</span>
                </div>
              </Link>
            )}
            {jwo.challanNumber && (
              <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Outward Challan</p>
                <div className="flex items-center gap-2.5 mt-2">
                  <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-blue-50 text-blue-600 shrink-0">
                    <i className="ri-file-text-line text-base" />
                  </div>
                  <span className="font-bold text-[#1e293b] text-sm">{jwo.challanNumber}</span>
                  <button onClick={() => window.print()} className="text-xs font-bold text-[#2563eb] hover:underline cursor-pointer ml-auto whitespace-nowrap">
                    <i className="ri-printer-line text-xs mr-1" />Print
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Approval Timeline */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
            <h2 className="text-sm font-bold text-[#1e293b] mb-4">Timeline</h2>
            <div className="space-y-0">
              {timelineItems.map((ti, idx) => (
                <div key={idx} className="flex gap-3 pb-4 last:pb-0">
                  <div className="flex flex-col items-center">
                    <div className={`w-3.5 h-3.5 rounded-full border-2 ${ti.active ? 'bg-[#2563eb] border-[#2563eb]' : 'bg-white border-slate-300'} shrink-0 mt-0.5`} />
                    {idx < timelineItems.length - 1 && (
                      <div className={`w-0.5 flex-1 min-h-[20px] ${ti.active ? 'bg-[#2563eb]' : 'bg-slate-200'}`} />
                    )}
                  </div>
                  <div className="pb-2">
                    <p className={`text-sm font-bold ${ti.active ? 'text-[#1e293b]' : 'text-slate-400'}`}>{ti.label}</p>
                    {ti.date && (
                      <p className="text-xs text-slate-500 font-medium">
                        {new Date(ti.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Record Receipt Slide-over */}
          {receiptOpen && (
            <div className="fixed inset-0 z-50 flex items-start justify-end">
              <div className="absolute inset-0 bg-black/40 backdrop-blur-xs" onClick={() => { setReceiptOpen(false); setSearchParams({}); }} />
              <div className="relative w-full max-w-lg bg-white h-full shadow-2xl overflow-y-auto animate-slide-in-right z-10 text-slate-900 border-l border-slate-200">
                <div className="sticky top-0 bg-white border-b border-slate-200 p-5 flex items-center justify-between z-10">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">Record Material Receipt</h3>
                    <p className="text-xs text-slate-500 font-medium">{jwo.jwoNumber} — {jwo.subContractorName}</p>
                  </div>
                  <button
                    onClick={() => { setReceiptOpen(false); setSearchParams({}); }}
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-500 cursor-pointer"
                  >
                    <i className="ri-close-line text-xl" />
                  </button>
                </div>

                <div className="p-5 space-y-5 bg-white">
                  {receiptError && (
                    <div className="flex items-center gap-2 bg-rose-50 border border-rose-200 rounded-xl p-3 text-sm text-rose-700 font-medium">
                      <i className="ri-error-warning-line text-lg text-rose-600" />
                      {receiptError}
                    </div>
                  )}

                  {jwo.items.map((item) => (
                    <div key={item.id} className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                      <p className="text-sm font-bold text-slate-900 mb-3">{item.itemName}</p>
                      <div className="flex items-center gap-1 text-xs text-slate-600 mb-3 font-medium">
                        <span>Sent: <strong className="text-slate-800">{item.sentQty} {item.unit}</strong></span>
                        <span className="mx-1.5">|</span>
                        <span>Pending: <strong className="text-amber-600 font-bold">{item.pendingQty} {item.unit}</strong></span>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-bold text-slate-700 mb-1">Received Qty</label>
                          <input
                            type="number"
                            min={0}
                            max={item.pendingQty}
                            value={receiptItems[item.id]?.receivedQty ?? 0}
                            onChange={(e) => {
                              const v = Math.max(0, parseInt(e.target.value) || 0);
                              setReceiptItems((prev) => ({ ...prev, [item.id]: { ...prev[item.id], receivedQty: Math.min(v, item.pendingQty - (prev[item.id]?.rejectedQty || 0)) } }));
                            }}
                            className="w-full px-3.5 py-2 text-sm border border-slate-200 rounded-xl bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-slate-700 mb-1">Rejected Qty</label>
                          <input
                            type="number"
                            min={0}
                            value={receiptItems[item.id]?.rejectedQty ?? 0}
                            onChange={(e) => {
                              const v = Math.max(0, parseInt(e.target.value) || 0);
                              setReceiptItems((prev) => ({ ...prev, [item.id]: { ...prev[item.id], rejectedQty: Math.min(v, item.pendingQty - (prev[item.id]?.receivedQty || 0)) } }));
                            }}
                            className="w-full px-3.5 py-2 text-sm border border-slate-200 rounded-xl bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium"
                          />
                        </div>
                      </div>
                      <div className="mt-3">
                        <label className="block text-xs font-bold text-slate-700 mb-1">Notes (rejection reason)</label>
                        <input
                          type="text"
                          value={receiptItems[item.id]?.notes ?? ''}
                          onChange={(e) => setReceiptItems((prev) => ({ ...prev, [item.id]: { ...prev[item.id], notes: e.target.value } }))}
                          placeholder="Optional notes..."
                          className="w-full px-3.5 py-2 text-sm border border-slate-200 rounded-xl bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium"
                        />
                      </div>
                    </div>
                  ))}

                  {jwo.jobCharges > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                      <i className="ri-information-line text-amber-500 text-lg mt-0.5" />
                      <div>
                        <p className="text-sm font-bold text-amber-900">Add job charges to production cost?</p>
                        <p className="text-xs text-amber-700 mt-0.5 font-medium">₹{jwo.jobCharges.toLocaleString('en-IN')} vendor processing charges</p>
                      </div>
                    </div>
                  )}

                  <button
                    onClick={handleRecordReceipt}
                    className="w-full py-3 rounded-xl bg-[#2563eb] text-white text-sm font-semibold hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap shadow-sm"
                  >
                    Record Receipt
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Partial Receipt Confirmation Modal */}
          {showPartialConfirm && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-black/40 backdrop-blur-xs" onClick={() => setShowPartialConfirm(false)} />
              <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 border border-slate-100">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-100 shrink-0">
                    <i className="ri-alert-line text-xl text-amber-600" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-[#1e293b]">Partial Receipt Confirmation</h3>
                    <p className="text-xs text-slate-500 mt-1 font-medium">
                      You have recorded receipt of only partial quantities.
                    </p>
                  </div>
                </div>
                <div className="space-y-2 mb-5">
                  {jwo.items.map((item) => {
                    const ri = receiptItems[item.id];
                    const recQty = ri?.receivedQty ?? 0;
                    const pending = item.pendingQty - recQty;
                    if (recQty >= item.pendingQty) return null;
                    return (
                      <div key={item.id} className="flex items-center justify-between bg-amber-50 rounded-xl px-3.5 py-2.5 text-xs border border-amber-200">
                        <span className="font-bold text-slate-800">{item.itemName}</span>
                        <span className="text-amber-800 font-semibold">
                          {recQty}/{item.pendingQty + item.receivedQty} {item.unit} — <strong>{pending} {item.unit}</strong> left
                        </span>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-500 mb-5 font-medium">
                  Status will be set to <span className="font-bold text-teal-700">PARTIALLY_RECEIVED</span>.
                  Remaining materials still with sub-contractor.
                </p>
                <div className="flex items-center gap-2.5">
                  <button
                    onClick={() => setShowPartialConfirm(false)}
                    className="flex-1 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50 cursor-pointer whitespace-nowrap"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={applyReceipt}
                    className="flex-1 py-2.5 rounded-xl bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 cursor-pointer whitespace-nowrap shadow-sm"
                  >
                    Confirm Partial Receipt
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Job Work Costing Modal */}
          {showCostingModal && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-black/40 backdrop-blur-xs" onClick={handleSkipCosting} />
              <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 border border-slate-100">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-teal-100 shrink-0">
                    <i className="ri-money-rupee-circle-line text-xl text-teal-600" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-[#1e293b]">Add Job Work Charges to Production Cost?</h3>
                    <p className="text-xs text-slate-400 font-medium mt-0.5">JWO: {jwo.jwoNumber}</p>
                  </div>
                </div>
                <div className="space-y-2 mb-5 bg-slate-50 rounded-xl p-3.5 text-xs font-medium border border-slate-200">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Sub-Contractor</span>
                    <span className="font-bold text-slate-800">{jwo.subContractorName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Job Charges</span>
                    <span className="font-bold text-[#2563eb]">₹{jwo.jobCharges.toLocaleString('en-IN')}</span>
                  </div>
                  {jwo.productionOrderId && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Linked Production Order</span>
                      <span className="font-bold text-slate-800">
                        {productionOrder?.poNumber || jwo.productionOrderId}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2.5">
                  <button
                    onClick={handleSkipCosting}
                    className="flex-1 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50 cursor-pointer whitespace-nowrap"
                  >
                    Skip
                  </button>
                  <button
                    onClick={handleAddToProductionCost}
                    className="flex-1 py-2.5 rounded-xl bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700 cursor-pointer whitespace-nowrap shadow-sm"
                  >
                    Add to Production Cost
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Print: Job Work Challan */}
          <div className="hidden print:block p-8 font-sans text-sm">
            <div className="border-2 border-gray-800 rounded p-6 max-w-[800px] mx-auto">
              <h2 className="text-center text-lg font-bold uppercase tracking-wide mb-1">Job Work Challan (Under GST)</h2>
              <p className="text-center text-xs text-gray-600 mb-4">Rule 45 — Goods sent for job work</p>
              <div className="grid grid-cols-2 gap-4 mb-4 text-xs">
                <div>
                  <p className="font-semibold">DC No: {jwo.challanNumber || '—'}</p>
                  <p>Date: {new Date(jwo.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                </div>
                <div className="text-right">
                  {jwo.status !== 'DRAFT' && <p className="text-amber-700 font-semibold">Status: {statusCfg.label}</p>}
                </div>
              </div>
              <div className="border-t border-b border-gray-300 py-3 mb-4 text-xs grid grid-cols-2 gap-4">
                <div>
                  <p className="font-semibold">From:</p>
                  <p>InvenPro Solutions Pvt. Ltd.</p>
                  <p>GSTIN: 27AABCU9603R1ZX</p>
                  <p>204, Tech Park, Baner, Pune 411045</p>
                </div>
                <div>
                  <p className="font-semibold">To:</p>
                  <p>{jwo.subContractorName}</p>
                  <p>GSTIN: {jwo.subContractorGSTIN}</p>
                </div>
              </div>
              <div className="mb-4 text-xs grid grid-cols-2 gap-4">
                <div>
                  <p><span className="font-semibold">Job Work:</span> {jwo.jobDescription}</p>
                </div>
                <div className="text-right">
                  <p><span className="font-semibold">Expected Return:</span> {new Date(jwo.expectedReturnDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                </div>
              </div>
              <table className="w-full text-xs border-collapse mb-4">
                <thead>
                  <tr className="border-y border-gray-400">
                    <th className="text-left py-1.5 px-2 font-semibold">#</th>
                    <th className="text-left py-1.5 px-2 font-semibold">Item</th>
                    <th className="text-left py-1.5 px-2 font-semibold">HSN</th>
                    <th className="text-right py-1.5 px-2 font-semibold">Qty</th>
                    <th className="text-right py-1.5 px-2 font-semibold">Rate</th>
                    <th className="text-right py-1.5 px-2 font-semibold">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {jwo.items.map((item, idx) => (
                    <tr key={item.id} className="border-b border-gray-200">
                      <td className="py-1.5 px-2">{idx + 1}</td>
                      <td className="py-1.5 px-2">{item.itemName}</td>
                      <td className="py-1.5 px-2">{item.hsnCode}</td>
                      <td className="py-1.5 px-2 text-right">{item.sentQty} {item.unit}</td>
                      <td className="py-1.5 px-2 text-right">₹{item.rate}</td>
                      <td className="py-1.5 px-2 text-right">₹{item.totalValue.toLocaleString('en-IN')}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray-400 font-bold">
                    <td colSpan={5} className="py-2 px-2 text-right">Total Material Value:</td>
                    <td className="py-2 px-2 text-right">₹{totalMaterialValue.toLocaleString('en-IN')}</td>
                  </tr>
                </tfoot>
              </table>
              {jwo.jobCharges > 0 && (
                <p className="text-xs mb-2"><span className="font-semibold">Job Charges:</span> ₹{jwo.jobCharges.toLocaleString('en-IN')}</p>
              )}
              <div className="border-t border-gray-300 pt-3 text-xs text-gray-600">
                <p>Note: Goods sent for job work under GST Challan Rule 45. No tax applicable.</p>
                <p>ITC-04 return to be filed quarterly.</p>
              </div>
              <div className="grid grid-cols-2 gap-8 mt-8 text-xs">
                <div>
                  <p className="border-t border-gray-400 pt-1 text-center">Received By (Sub-Contractor)</p>
                </div>
                <div>
                  <p className="border-t border-gray-400 pt-1 text-center">Authorized Signatory (InvenPro)</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}